// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { Layout, Typography, Row, Col, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  FaChartLine,
  FaUsers,
  FaStore,
  FaBook,
  FaProjectDiagram,
  FaBell,
  FaMoneyBillWave,
  FaCertificate,
} from 'react-icons/fa';
import api from '../../api/axios'; // Ensure this path is correct
import DashboardHeader from './DashboardHeader'; // Import DashboardHeader
import OverviewStats from './OverviewStats';
import UsersList from './UsersList';
import VendorsList from './Vendors/VendorsList';
import CoursesList from './CoursesList';
import ProjectsList from './ProjectsList';
import SystemAlerts from './SystemAlerts';
import TransactionsList from './Vendors/TransactionsList';
// import CertificatesList from './CertificatesList';
import DashboardFooter from './DashboardFooter';

const { Content } = Layout;
const { Title } = Typography;

const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false); // For sidebar toggle (optional)

  // Mock logged-in user data (replace with real user data from auth context or API)
  const loggedInUser = {
    name: 'Admin User', // Replace with actual user data
  };

  // Initial static data
  const initialUsers = [
    { id: 1, name: 'John Doe', role: 'STUDENT', status: 'Active', email: 'john@example.com' },
    { id: 2, name: 'Jane Smith', role: 'MENTOR', status: 'Active', email: 'jane@example.com' },
    { id: 3, name: 'Bob Johnson', role: 'VENDOR', status: 'Pending', email: 'bob@example.com' },
    { id: 4, name: 'Alice Brown', role: 'STUDENT', status: 'Active', email: 'alice@example.com' },
    { id: 5, name: 'Mike Wilson', role: 'MENTOR', status: 'Inactive', email: 'mike@example.com' },
  ];

  const initialVendors = [
    { id: 1, name: 'TechAI Labs', products: 5, status: 'Active', revenue: 5000 },
    { id: 2, name: 'SecureAI', products: 3, status: 'Pending', revenue: 0 },
    { id: 3, name: 'DataCorp', products: 4, status: 'Active', revenue: 3000 },
  ];

  const initialAlerts = [
    { id: 1, message: 'New vendor registration pending approval', date: '2025-03-05 09:00', status: 'Pending' },
    { id: 2, message: 'System maintenance scheduled', date: '2025-03-06 02:00', status: 'Resolved' },
  ];

  const initialTransactions = [
    { id: 1, type: 'Product Sale', amount: 99, date: '2025-03-01', userId: 1 },
    { id: 2, type: 'Donation', amount: 50, date: '2025-03-02', userId: 2 },
    { id: 3, type: 'Course Fee', amount: 150, date: '2025-03-03', userId: 4 },
  ];

  const initialCourses = [
    { id: 1, title: 'AI Basics', status: 'Published', enrollments: 150 },
    { id: 2, title: 'Cybersecurity 101', status: 'Draft', enrollments: 0 },
    { id: 3, title: 'Data Science Intro', status: 'Published', enrollments: 80 },
  ];

  const initialProjects = [
    { id: 1, title: 'AI Chatbot', status: 'In Progress', userId: 1, deadline: '2025-04-01' },
    { id: 2, title: 'Portfolio Website', status: 'Completed', userId: 2, deadline: '2025-03-01' },
    { id: 3, title: 'Smart City App', status: 'In Progress', userId: 4, deadline: '2025-05-01' },
  ];

  const initialCertificates = [
    { id: 1, title: 'AI Basics Certificate', userId: 1, issued: '2025-03-01' },
    { id: 2, title: 'Cybersecurity 101 Certificate', userId: 2, issued: '2025-03-02' },
    { id: 3, title: 'Data Science Intro Certificate', userId: 4, issued: '2025-03-04' },
  ];

  useEffect(() => {
    // Load initial static data (replace with API calls in production)
    setUsers(initialUsers);
    setVendors(initialVendors);
    setSystemAlerts(initialAlerts);
    setTransactions(initialTransactions);
    setCourses(initialCourses);
    setProjects(initialProjects);
    setCertificates(initialCertificates);

    // Example API calls (uncomment and adjust as needed)
    /*
    api.get('/users').then((response) => setUsers(response.data)).catch(console.error);
    api.get('/vendors').then((response) => setVendors(response.data)).catch(console.error);
    api.get('/alerts').then((response) => setSystemAlerts(response.data)).catch(console.error);
    api.get('/transactions').then((response) => setTransactions(response.data)).catch(console.error);
    api.get('/courses').then((response) => setCourses(response.data)).catch(console.error);
    api.get('/projects').then((response) => setProjects(response.data)).catch(console.error);
    api.get('/certificates').then((response) => setCertificates(response.data)).catch(console.error);
    */
  }, []);

  // Calculate dashboard metrics
  const mentorsCount = users.filter((user) => user.role === 'MENTOR' && user.status === 'Active').length;
  const studentsCount = users.filter((user) => user.role === 'STUDENT' && user.status === 'Active').length;
  const activeVendorsCount = vendors.filter((vendor) => vendor.status === 'Active').length;
  const publishedCoursesCount = courses.filter((course) => course.status === 'Published').length;
  const activeProjectsCount = projects.filter((project) => project.status === 'In Progress').length;
  const pendingAlertsCount = systemAlerts.filter((alert) => alert.status === 'Pending').length;
  const totalRevenue = transactions.reduce((sum, transaction) => sum + transaction.amount, 0);
  const issuedCertificatesCount = certificates.length;

  const overviewData = {
    totalUsers: users.length,
    mentors: mentorsCount,
    students: studentsCount,
    vendors: activeVendorsCount,
    courses: publishedCoursesCount,
    projects: activeProjectsCount,
    alerts: pendingAlertsCount,
    revenue: totalRevenue,
    certificates: issuedCertificatesCount,
  };

  const handleApproveVendor = (vendorId) => {
    console.log(`Approving vendor ${vendorId}`);
    // Add API call to update vendor status
    // api.put(`/vendors/${vendorId}`, { status: 'Active' }).then(() => {
    //   setVendors(vendors.map(v => v.id === vendorId ? { ...v, status: 'Active' } : v));
    // });
  };

  const handleDeleteUser = (userId) => {
    console.log(`Deleting user ${userId}`);
    setUsers(users.filter((user) => user.id !== userId));
    // Add API call to delete user
    // api.delete(`/users/${userId}`).catch(console.error);
  };

  const showDrawer = () => {
    setDrawerVisible(true); // Optional: Toggle for sidebar if implemented
  };

  // Handle logout to align with DashboardHeader
  const handleLogout = () => {
    localStorage.removeItem('userRole');
    if (onLogout) onLogout(); // Call parent logout function if provided
    navigate('/signin');
  };

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* DashboardHeader is already responsive */}
      <DashboardHeader showDrawer={showDrawer} loggedInUser={loggedInUser} />

      <Content
        style={{
          margin: '16px', // Reduced margin for smaller screens
          padding: 0,
          minHeight: 'calc(100vh - 144px)', // Adjusted for header (64px) + reduced margins
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            padding: '16px', // Reduced padding for mobile
            background: '#fff',
            borderRadius: 8,
            fontSize: '14px', // Slightly smaller font for mobile
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            maxWidth: '1200px', // Cap width for larger screens
            margin: '0 auto', // Center the content
          }}
        >
          {/* Overview Stats */}
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card
                title={<span><FaChartLine style={{ marginRight: 8 }} /> Overview</span>}
                bordered={false}
                style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)' }}
              >
                <OverviewStats data={overviewData} />
              </Card>
            </Col>
          </Row>

          {/* Two-Column Layout for Lists, Stacking on Mobile */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Card
                title={<span><FaUsers style={{ marginRight: 8 }} /> Users</span>}
                bordered={false}
                style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)' }}
              >
                <UsersList users={users} handleDeleteUser={handleDeleteUser} navigate={navigate} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Card
                title={<span><FaStore style={{ marginRight: 8 }} /> Vendors</span>}
                bordered={false}
                style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)' }}
              >
                <VendorsList vendors={vendors} handleApproveVendor={handleApproveVendor} navigate={navigate} />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Card
                title={<span><FaBook style={{ marginRight: 8 }} /> Courses</span>}
                bordered={false}
                style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)' }}
              >
                <CoursesList courses={courses} navigate={navigate} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Card
                title={<span><FaProjectDiagram style={{ marginRight: 8 }} /> Projects</span>}
                bordered={false}
                style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)' }}
              >
                <ProjectsList projects={projects} users={users} navigate={navigate} />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Card
                title={<span><FaBell style={{ marginRight: 8 }} /> System Alerts</span>}
                bordered={false}
                style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)' }}
              >
                <SystemAlerts systemAlerts={systemAlerts} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Card
                title={<span><FaMoneyBillWave style={{ marginRight: 8 }} /> Transactions</span>}
                bordered={false}
                style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)' }}
              >
                <TransactionsList transactions={transactions} />
              </Card>
            </Col>
          </Row>

          {/* <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card
                title={<span><FaCertificate style={{ marginRight: 8 }} /> Certificates</span>}
                bordered={false}
                style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)' }}
              >
                <CertificatesList certificates={certificates} users={users} />
              </Card>
            </Col>
          </Row> */}
        </div>
      </Content>
      <DashboardFooter />
    </Layout>
  );
};

export default AdminDashboard;